import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Search } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover/index.js";
import { cn } from "../../lib/utils.js";
import { listIanaTimezones } from "./iana-timezones.js";
import {
  buildTimezoneOptions,
  filterTimezoneOptions,
  formatTimezoneLabel,
} from "./timezone-labels.js";

export function TimezoneSelect({
  value,
  onChange,
  disabled = false,
  id,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const options = useMemo(() => buildTimezoneOptions(listIanaTimezones()), []);
  const filtered = useMemo(() => filterTimezoneOptions(options, query), [options, query]);
  const selectedLabel = value ? formatTimezoneLabel(value) : "—";

  function selectZone(zone: string): void {
    onChange(zone);
    setOpen(false);
    setQuery("");
  }

  return (
    <Popover
      open={disabled ? false : open}
      onOpenChange={(next) => {
        if (disabled) return;
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          className={cn(
            "flex h-10 w-full min-w-0 cursor-pointer items-center gap-2 rounded-lg border border-line-strong bg-surface px-3 text-start text-[0.8125rem] text-foreground",
            "focus-visible:border-accent focus-visible:shadow-[0_0_0_3px_var(--color-ring)] focus-visible:outline-none",
            "disabled:cursor-not-allowed disabled:opacity-70",
          )}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={t("settings.general.timezoneLabel")}
          disabled={disabled}
          data-testid="settings-general-timezone"
          data-timezone-id={value || undefined}
        >
          <span className="min-w-0 flex-1 truncate" data-testid="settings-general-timezone-label">
            {selectedLabel}
          </span>
          <ChevronDown size={16} className="shrink-0 opacity-70" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={4}
        className="w-[min(26rem,calc(100vw-1.5rem))] min-w-[18rem] rounded-lg border-border p-2"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          searchInputRef.current?.focus();
        }}
      >
        <div className="grid gap-2">
          <label className="relative block">
            <Search
              size={14}
              className="pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-muted"
              aria-hidden="true"
            />
            <input
              ref={searchInputRef}
              type="search"
              className="h-9 w-full rounded-md border border-border bg-surface ps-8 pe-3 text-start text-xs"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("settings.general.timezoneSearch")}
              disabled={disabled}
              aria-label={t("settings.general.timezoneSearch")}
              data-testid="settings-general-timezone-search"
            />
          </label>
          <ul
            className="m-0 max-h-[min(18rem,50vh)] list-none overflow-y-auto rounded-md border border-border bg-surface p-0"
            role="listbox"
            aria-label={t("settings.general.timezoneLabel")}
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-[0.8125rem] text-muted">
                {t("settings.general.timezoneEmpty")}
              </li>
            ) : (
              filtered.map((option) => {
                const selected = option.id === value;
                return (
                  <li key={option.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      data-timezone-option={option.id}
                      className={cn(
                        "flex w-full cursor-pointer items-center border-0 bg-transparent px-3 py-2 text-start text-[0.8125rem] text-inherit not-disabled:hover:bg-accent/10",
                        selected && "bg-accent/10 font-semibold",
                      )}
                      onClick={() => selectZone(option.id)}
                      disabled={disabled}
                    >
                      <span className="min-w-0 flex-1 truncate">{option.label}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
}
